'use client';

import { useStudioVideoPlay } from '@/api/queries/studio.queries';
import {
  deriveVideoPillState,
  VideoStatusPill,
} from '@/app/studio/videos/[uuid]/_components/VideoStatusPill';
import { canSeeTravelKnowledge } from '@/shared/lib/auth/permissions';
import { useAuthStore } from '@/shared/stores/auth-store';
import { getVideoTitleDisplay } from '@/shared/utils/video-title';
import { MapPinned, MessageSquare, Settings } from 'lucide-react';
import { ContextualSidebarShell } from './ContextualSidebarShell';
import type { ContextualNavItem, ContextualSidebarComponentProps } from './types';

/**
 * Video-scoped sidebar content. Mounted by StudioSidebar when pathname matches
 * `/studio/videos/<uuid>/`. Shares the video query cache with VideoDetailShell
 * via TanStack — no extra network call.
 */
export function VideoSidebarContent({ paramValue: uuid }: ContextualSidebarComponentProps) {
  const { data: video } = useStudioVideoPlay(uuid);
  const user = useAuthStore((s) => s.user);

  const navItems: ContextualNavItem[] = [
    {
      id: 'details',
      label: 'Details',
      href: `/studio/videos/${uuid}/details`,
      icon: Settings,
    },
    {
      id: 'comments',
      label: 'Comments',
      href: `/studio/videos/${uuid}/comments`,
      icon: MessageSquare,
    },
    // TEMP — gated to allow-listed testers until Travel Tips ships to all.
    ...(canSeeTravelKnowledge(user)
      ? [
          {
            id: 'travel-tips',
            label: 'Travel Tips',
            href: `/studio/videos/${uuid}/travel-tips`,
            icon: MapPinned,
          },
        ]
      : []),
  ];

  return (
    <ContextualSidebarShell
      backHref="/studio/content"
      backLabel="Channel content"
      entity={{
        thumbnail: video?.thumbnail ?? null,
        title: video ? getVideoTitleDisplay(video.title) : undefined,
        eyebrow: 'Your video',
        thumbnailAspectRatio: '16/9',
        statusPill: video ? <VideoStatusPill state={deriveVideoPillState(video)} /> : null,
      }}
      navItems={navItems}
    />
  );
}
